import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Globe, ChevronUp, ChevronDown, GripVertical, Save, Plus, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import { brandsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminBrands = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBrand, setNewBrand] = useState({
    name: '',
    country: '',
    specialty: '',
    website: '',
    heroImage: '',
    about: '',
    features: ''
  });

  // Delete confirmation state
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await brandsApi.getAllAdmin();
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const moveBrand = (index, direction) => {
    const newBrands = [...brands];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newBrands.length) return;
    
    [newBrands[index], newBrands[targetIndex]] = [newBrands[targetIndex], newBrands[index]];
    
    newBrands.forEach((brand, idx) => {
      brand.order = idx + 1;
    });
    
    setBrands(newBrands);
    setHasChanges(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const orderUpdates = brands.map((brand, idx) => ({
        id: brand.id,
        order: idx + 1
      }));
      await brandsApi.updateOrder(orderUpdates);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving order:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const brandData = {
        ...newBrand,
        features: newBrand.features.split('\n').filter(f => f.trim())
      };
      const response = await brandsApi.create(brandData);
      setShowCreateModal(false);
      setNewBrand({
        name: '',
        country: '',
        specialty: '',
        website: '',
        heroImage: '',
        about: '',
        features: ''
      });
      // Navigate to edit page to add content
      navigate(`/admin/brands/${response.data.id}`);
    } catch (error) {
      console.error('Error creating brand:', error);
      alert('Error creating brand. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBrand = async () => {
    if (!brandToDelete) return;
    setDeleting(true);
    try {
      await brandsApi.delete(brandToDelete.id);
      setBrands(brands.filter(b => b.id !== brandToDelete.id));
      setBrandToDelete(null);
    } catch (error) {
      console.error('Error deleting brand:', error);
      alert('Error deleting brand. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisibility = async (brandId) => {
    try {
      const res = await brandsApi.toggleVisibility(brandId);
      setBrands(brands.map(b => b.id === brandId ? { ...b, hidden: res.data.hidden } : b));
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="brands-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-brands-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Manage Brands</h1>
        <div className="flex gap-3">
          {hasChanges && (
            <Button 
              onClick={saveOrder} 
              disabled={saving}
              data-testid="save-order-btn"
              variant="outline"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Order'}
            </Button>
          )}
          <Button 
            onClick={() => setShowCreateModal(true)}
            data-testid="create-brand-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Brand
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          You have unsaved changes to the brand order. Click "Save Order" to apply.
        </div>
      )}

      <div className="space-y-4">
        {brands.map((brand, index) => (
          <div
            key={brand.id}
            className={`bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group flex ${brand.hidden ? 'opacity-60 ring-1 ring-amber-300' : ''}`}
            data-testid={`brand-item-${brand.id}`}
          >
            {/* Reorder Controls */}
            <div className="flex flex-col items-center justify-center px-3 bg-gray-50 border-r">
              <button
                onClick={() => moveBrand(index, 'up')}
                disabled={index === 0}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`move-up-${brand.id}`}
                aria-label={`Move ${brand.name} up`}
              >
                <ChevronUp className="h-5 w-5 text-gray-600" />
              </button>
              <GripVertical className="h-5 w-5 text-gray-400 my-1" />
              <button
                onClick={() => moveBrand(index, 'down')}
                disabled={index === brands.length - 1}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`move-down-${brand.id}`}
                aria-label={`Move ${brand.name} down`}
              >
                <ChevronDown className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Brand Card Content */}
            <Link
              to={`/admin/brands/${brand.id}`}
              className="flex-1 flex"
            >
              <div className="w-48 h-32 relative flex-shrink-0">
                <img
                  src={brand.heroImage || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop'}
                  alt={brand.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
              </div>
              
              <div className="flex-1 p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                      #{index + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">{brand.name}</h3>
                    {brand.hidden && (
                      <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{brand.country} • {brand.specialty}</p>
                  <div className="flex items-center text-sm text-gray-400 mt-2">
                    <Globe className="h-4 w-4 mr-1" />
                    {brand.website || 'No website set'}
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-900 flex items-center">
                  Edit Brand
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            {/* Visibility & Delete Buttons */}
            <div className="flex flex-col items-center justify-center gap-2 px-4 border-l bg-gray-50">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleToggleVisibility(brand.id);
                }}
                className={`p-2 rounded transition-colors ${brand.hidden ? 'hover:bg-green-100 text-amber-500 hover:text-green-600' : 'hover:bg-amber-100 text-gray-400 hover:text-amber-600'}`}
                data-testid={`toggle-visibility-${brand.id}`}
                title={brand.hidden ? 'Show brand (make public)' : 'Hide brand (restrict from public)'}
              >
                {brand.hidden ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setBrandToDelete(brand);
                }}
                className="p-2 hover:bg-red-100 rounded text-gray-400 hover:text-red-600 transition-colors"
                data-testid={`delete-${brand.id}`}
                aria-label={`Delete ${brand.name}`}
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {brands.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 mb-4">No brands yet. Create your first brand to get started.</p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add New Brand
          </Button>
        </div>
      )}

      <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
        <strong>Tip:</strong> Use the arrow buttons to reorder brands. Click on a brand to edit its details, news, products, and projects. Don't forget to save your order changes!
      </div>

      {/* Create Brand Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Brand</DialogTitle>
            <DialogDescription>
              Create a new brand. You can add news, products, and projects after creation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateBrand} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Brand Name *</Label>
                <Input
                  id="name"
                  required
                  value={newBrand.name}
                  onChange={(e) => setNewBrand({...newBrand, name: e.target.value})}
                  placeholder="e.g. Acme Lighting"
                  data-testid="new-brand-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  required
                  value={newBrand.country}
                  onChange={(e) => setNewBrand({...newBrand, country: e.target.value})}
                  placeholder="e.g. Germany"
                  data-testid="new-brand-country"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="specialty">Specialty *</Label>
              <Input
                id="specialty"
                required
                value={newBrand.specialty}
                onChange={(e) => setNewBrand({...newBrand, specialty: e.target.value})}
                placeholder="e.g. Architectural LED Lighting"
                data-testid="new-brand-specialty"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website URL</Label>
              <Input
                id="website"
                value={newBrand.website}
                onChange={(e) => setNewBrand({...newBrand, website: e.target.value})}
                placeholder="https://www.example.com"
                data-testid="new-brand-website"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="heroImage">Hero Image URL</Label>
              <Input
                id="heroImage"
                value={newBrand.heroImage}
                onChange={(e) => setNewBrand({...newBrand, heroImage: e.target.value})}
                placeholder="https://images.unsplash.com/..."
                data-testid="new-brand-hero"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="about">About Description</Label>
              <Textarea
                id="about"
                rows={4}
                value={newBrand.about}
                onChange={(e) => setNewBrand({...newBrand, about: e.target.value})}
                placeholder="A detailed description of the brand..."
                data-testid="new-brand-about"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="features">Key Features (one per line)</Label>
              <Textarea
                id="features"
                rows={3}
                value={newBrand.features}
                onChange={(e) => setNewBrand({...newBrand, features: e.target.value})}
                placeholder="Made in Germany&#10;Energy efficient&#10;Custom solutions"
                data-testid="new-brand-features"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="submit-new-brand">
                {creating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" />Create Brand</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!brandToDelete} onOpenChange={() => setBrandToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Brand</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{brandToDelete?.name}</strong>? This will also delete all associated news, products, and projects. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBrand}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-brand"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                'Delete Brand'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBrands;
